import React from 'react';
import { motion } from 'framer-motion';
import { ShieldCheck, Command } from 'lucide-react';
import { Icon } from '@/components/ui/icon';
import { Badge } from '@/components/ui/badge';

const AuthLeftPanel = ({ titlePrefix, titleSuffix }) => {
  return (
    <div className="hidden lg:flex lg:w-1/2 xl:w-3/5 relative overflow-hidden">
      {/* Background gradient */}
      <div
        className="absolute inset-0"
        style={{
          background: 'linear-gradient(135deg, hsl(239 84% 14%) 0%, hsl(239 84% 8%) 60%, hsl(222 47% 4%) 100%)',
        }}
      />
      {/* Mesh grid */}
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `repeating-linear-gradient(0deg, transparent, transparent 40px, rgba(255,255,255,1) 40px, rgba(255,255,255,1) 41px),
            repeating-linear-gradient(90deg, transparent, transparent 40px, rgba(255,255,255,1) 40px, rgba(255,255,255,1) 41px)`,
        }}
      />
      {/* Glow orbs */}
      <div
        className="absolute -top-32 -left-32 w-96 h-96 rounded-full blur-3xl opacity-20"
        style={{ backgroundColor: '#6366f1' }}
      />
      <div
        className="absolute -bottom-20 right-20 w-80 h-80 rounded-full blur-3xl opacity-15"
        style={{ backgroundColor: '#8b5cf6' }}
      />

      <div className="relative z-10 flex flex-col justify-between p-12 w-full">
        {/* Logo */}
        <div className="flex items-center gap-3">
          <Badge className="rounded-xl p-2">
            <Icon icon={Command} color="white" />
          </Badge>
          <span className="font-black text-white text-xl tracking-tight">ShadeX</span>
        </div>

        {/* Center content */}
        <div className="space-y-6">
          <div className="space-y-3">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 backdrop-blur border border-white/20 text-xs font-medium text-white/80"
            >
              <Icon icon={ShieldCheck} color="success" size="sm" />
              Enterprise-grade security
            </motion.div>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
              className="text-4xl xl:text-5xl font-black text-white leading-tight"
            >
              {titlePrefix}
              <br />
              <span className="text-transparent bg-clip-text" style={{ backgroundImage: 'linear-gradient(90deg, #818cf8, #c084fc)' }}>
                {titleSuffix}
              </span>
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-white/50 text-base max-w-sm"
            >
              A fully-featured, beautifully crafted admin dashboard with shadcn/ui components, powerful analytics, and an intuitive user experience.
            </motion.p>
          </div>

          {/* Feature tags */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="flex flex-wrap gap-2"
          >
            {['Dark & Light Mode', '10+ Color Themes', 'RTL Support', 'Recharts Analytics', 'shadcn/ui'].map(tag => (
              <span key={tag} className="px-3 py-1 rounded-full bg-white/8 border border-white/12 text-xs text-white/60">
                {tag}
              </span>
            ))}
          </motion.div>
        </div>

        {/* Footer note */}
        <p className="text-xs text-white/30">
          © {new Date().getFullYear()} ShadeX Inc. — All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default AuthLeftPanel;
